import React, { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { errText } from "@/components/servicedesk/SdBits";
import { useBlob, imgPath } from "@/components/servicedesk/SdNews";

const KINDS = [["article", "News / information text"], ["pdf", "PDF document"], ["link", "Web link"]];

const upload = async (slot, kind, file, setF) => {
  const fd = new FormData(); fd.append("file", file);
  try { const { data } = await api.post(`/servicedesk/news/${slot}/${kind}`, fd); setF((x) => ({ ...x, ...Object.fromEntries(Object.entries(data).filter(([k]) => k.startsWith(kind) || k.startsWith("has_"))) })); toast.success(kind === "pdf" ? "PDF uploaded" : "Image uploaded"); }
  catch (e) { toast.error(errText(e, "Upload failed")); }
};

const SlotEditor = ({ row }) => {
  const [f, setF] = useState({ title: "", summary: "", kind: "article", body: "", url: "", active: true, ...row });
  const img = useBlob(imgPath(f));
  const s = f.slot;
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const removeImg = async () => {
    try { await api.delete(`/servicedesk/news/${s}/image`); setF((x) => ({ ...x, has_image: false, image_v: undefined, image_name: undefined })); toast.success("Tile image removed"); }
    catch (e) { toast.error(errText(e, "Could not remove image")); }
  };
  const save = async () => {
    try { const { data } = await api.put(`/servicedesk/news/${s}`, { title: f.title, summary: f.summary, kind: f.kind, body: f.body, url: f.url, active: f.active }); setF({ ...f, ...data }); toast.success(`Tile ${s} saved`); }
    catch (e) { toast.error(errText(e)); }
  };
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3" data-testid={`sd-news-slot-${s}`}>
      <div className="flex items-center justify-between"><h3 className="text-sm font-semibold">Tile {s}</h3>
        <label className="flex items-center gap-2 text-xs text-slate-500">Show on Submit page<Switch checked={f.active} onCheckedChange={set("active")} data-testid={`sd-news-active-${s}`} /></label></div>
      <div className="flex gap-3 items-start">
        <div className="w-28 aspect-[4/3] rounded-lg bg-slate-100 overflow-hidden shrink-0 grid place-items-center">{img ? <img src={img} alt="" className="w-full h-full object-cover" /> : <Icons.Image className="h-6 w-6 text-slate-300" />}</div>
        <div className="space-y-1.5 flex-1"><Label className="text-xs">Tile image (JPG or PNG, max 2 MB)</Label>
          <Input type="file" accept="image/jpeg,image/png" onChange={(e) => {
            const file = e.target.files[0]; e.target.value = "";
            if (!file) return;
            if (file.size > 2 * 1024 * 1024) { toast.error("Tile image must be 2 MB or smaller"); return; }
            upload(s, "image", file, setF);
          }} data-testid={`sd-news-image-${s}`} />
          {f.has_image && <Button type="button" size="sm" variant="outline" className="text-red-600" onClick={removeImg} data-testid={`sd-news-image-remove-${s}`}><Icons.Trash2 className="h-3.5 w-3.5 mr-1" />Remove image</Button>}</div>
      </div>
      <div className="space-y-1.5"><Label className="text-xs">Clickable title *</Label><Input value={f.title} onChange={set("title")} placeholder="e.g. Scheduled email maintenance this Saturday" data-testid={`sd-news-title-${s}`} /></div>
      <div className="space-y-1.5"><Label className="text-xs">Short caption</Label><Input value={f.summary} onChange={set("summary")} data-testid={`sd-news-summary-${s}`} /></div>
      <div className="space-y-1.5"><Label className="text-xs">Opens</Label>
        <Select value={f.kind} onValueChange={set("kind")}><SelectTrigger data-testid={`sd-news-kind-${s}`}><SelectValue /></SelectTrigger>
          <SelectContent>{KINDS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select></div>
      {f.kind === "pdf" && <div className="space-y-1.5"><Label className="text-xs">PDF (max 10 MB){f.pdf_name ? ` · current: ${f.pdf_name}` : ""}</Label>
        <Input type="file" accept="application/pdf" onChange={(e) => e.target.files[0] && upload(s, "pdf", e.target.files[0], setF)} data-testid={`sd-news-pdf-${s}`} /></div>}
      {f.kind === "link" && <div className="space-y-1.5"><Label className="text-xs">Link URL</Label><Input value={f.url} onChange={set("url")} placeholder="https://" data-testid={`sd-news-url-${s}`} /></div>}
      {f.kind !== "pdf" && <div className="space-y-1.5"><Label className="text-xs">{f.kind === "link" ? "Note shown before the link" : "News / information content"}</Label>
        <Textarea rows={f.kind === "link" ? 2 : 5} value={f.body} onChange={set("body")} data-testid={`sd-news-body-${s}`} /></div>}
      <Button size="sm" onClick={save} data-testid={`sd-news-save-${s}`}>Save tile {s}</Button>
    </div>
  );
};

export const NewsTab = () => {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/servicedesk/news", { params: { all: true } }).then(({ data }) => setRows(data)).catch(() => setRows([])); }, []);
  if (!rows) return <div className="text-sm text-slate-500">Loading…</div>;
  return (
    <div className="space-y-3" data-testid="sd-news-settings">
      <p className="text-xs text-slate-500">These 4 tiles appear beside the Submit a Ticket form. Each tile has an image and a clickable title that opens a PDF, news text or a link. Each tile saves on its own.</p>
      <div className="grid lg:grid-cols-2 gap-4">{rows.map((r) => <SlotEditor key={r.slot} row={r} />)}</div>
    </div>
  );
};
